import { c as _c } from "react/compiler-runtime";
import type { HTMLAttributeReferrerPolicy, JSX } from 'react';
import styles from './CmsVideoEmbed.module.css';
export interface CmsVideoEmbedIframeAttrs {
  frameBorder?: string | number;
  allowFullscreen?: boolean;
  allow?: string;
  referrerPolicy?: HTMLAttributeReferrerPolicy;
  loading?: 'lazy' | 'eager';
}
export interface CmsVideoEmbedProps {
  src: string;
  title: string;
  provider?: 'youtube' | 'vimeo' | 'wistia';
  aspectRatio?: '16:9' | '4:3';
  iframeAttrs?: CmsVideoEmbedIframeAttrs;
  blockId?: string;
  className?: string;
  fieldId?: string;
  /**
   * 'safe' (default): apply YouTube-friendly `allow`, `referrerPolicy`, and
   *   `allowFullScreen=true` defaults when `iframeAttrs` does not set them.
   *   Use for new KB pages and any caller that has not already shipped a
   *   stricter iframe attribute set.
   * 'minimal': only emit attributes the caller passes in `iframeAttrs`. Use
   *   for callers that need byte-exact iframe parity (e.g. existing FAQ pages
   *   that ship without `allow`/`referrerPolicy`).
   * `loading` is always `lazy` by default in both modes because every caller
   *   in this repo lazy-loads iframes.
   */
  defaults?: 'safe' | 'minimal';
}
const DEFAULT_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
const DEFAULT_REFERRER_POLICY: HTMLAttributeReferrerPolicy = 'strict-origin-when-cross-origin';
export function CmsVideoEmbed(t0) {
  const $ = _c(15);
  const {
    src,
    title,
    aspectRatio,
    iframeAttrs,
    blockId,
    className,
    fieldId,
    defaults: t1
  } = t0;
  const defaults = t1 === undefined ? "safe" : t1;
  const isSafe = defaults === "safe";
  const allow = iframeAttrs?.allow ?? (isSafe ? DEFAULT_ALLOW : undefined);
  const referrerPolicy = iframeAttrs?.referrerPolicy ?? (isSafe ? DEFAULT_REFERRER_POLICY : undefined);
  const allowFullScreen = iframeAttrs?.allowFullscreen ?? (isSafe ? true : undefined);
  const loading = iframeAttrs?.loading ?? "lazy";
  const t2 = iframeAttrs?.frameBorder;
  const t3 = aspectRatio ? undefined : blockId;
  let t4;
  if ($[0] !== allow || $[1] !== allowFullScreen || $[2] !== className || $[3] !== fieldId || $[4] !== loading || $[5] !== referrerPolicy || $[6] !== src || $[7] !== t2 || $[8] !== t3 || $[9] !== title) {
    t4 = <iframe src={src} title={title} className={className} loading={loading} referrerPolicy={referrerPolicy} allow={allow} allowFullScreen={allowFullScreen} frameBorder={t2} data-cms-block-id={t3} data-cms-field={fieldId} />;
    $[0] = allow;
    $[1] = allowFullScreen;
    $[2] = className;
    $[3] = fieldId;
    $[4] = loading;
    $[5] = referrerPolicy;
    $[6] = src;
    $[7] = t2;
    $[8] = t3;
    $[9] = title;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const iframe = t4;
  if (aspectRatio) {
    const wrapClass = aspectRatio === "4:3" ? styles.ratio4x3 : styles.ratio16x9;
    let t5;
    if ($[11] !== blockId || $[12] !== iframe || $[13] !== wrapClass) {
      t5 = <div className={wrapClass} data-cms-block-id={blockId}>{iframe}</div>;
      $[11] = blockId;
      $[12] = iframe;
      $[13] = wrapClass;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  return iframe;
}